import {BaseSyntheticEvent, FC, useEffect, useState} from "react"
import {Button} from "@fluentui/react-northstar"
import {debounce} from "lodash"

import {Business, CompanyValue, Occasion, Recognition, User, RecognitionSource} from "../../types"
import recognitionService from "../../services/recognition"
import businessService from "../../services/business"
import {submitTask} from "../../utils/msteams"
import userService from "../../services/user"
import Users from "./Users"
import RecognitionMessage from "./RecognitionMessage"
import CompanyCelebration from "./CompanyCelebration"
import CompanyValues from "./CompanyValues"
import trackingService from "../../services/tracking"
import {CustomForm, FieldsContainer, FormLabel, CancelButtonWrapper} from "./styles"
import {getDomain} from "helpers/auth/createLink"
import {CtaContainer} from "../ui"
import {app} from "@microsoft/teams-js";

export interface Pair {
  name: string,
  uuid: string
}

interface FormData {
  companyValues?: Pair[],
  message?: string,
  occasionId?: string,
  users?: Pair[],
}

interface FormValidationErrors {
  isMessageEmpty?: boolean,
  isUserListEmpty?: boolean,
}

const RecognitionForm: FC = () => {
  const [business, setBusiness] = useState<Business>()
  const [companyValues, setCompanyValues] = useState<CompanyValue[]>([])
  const [occasions, setOccasions] = useState<Occasion[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [currentUser, setCurrentUser] = useState<User>()
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [formData, setFormData] = useState<FormData>({})
  const [validationErrors, setValidationErrors] = useState<FormValidationErrors>({})

  useEffect(() => {
    businessService.getCurrentBusiness().then((b) => {
      setBusiness(b)
      Promise.all([recognitionService.getCompanyValues(b.uuid),
        recognitionService.getOccasions(b.uuid),
        userService.getCurrentUser()])
        .then(([c, o, currentUser]) => {
          setCompanyValues(c)
          setOccasions(o)
          setCurrentUser(currentUser)
          trackingService.startCreateRecognition({type: "task_module"})
          app.initialize().then(() => app.notifySuccess())
        })
        .catch(() => {
          app.initialize().then(() => app.notifyFailure({reason: app.FailedReason.Timeout}))
        })
    }).catch(() => {
      app.initialize().then(() => app.notifyFailure({reason: app.FailedReason.Timeout}))
    })
  }, [])

  const validateForm = (data: FormData): boolean => {
    let valid = true
    const errors: FormValidationErrors = {}

    if (!data.users || !data.users.length) {
      errors.isUserListEmpty = true
      valid = false
    }

    if (!data.message) {
      errors.isMessageEmpty = true
      valid = false
    }
    setValidationErrors(errors)
    return valid
  }

  const getAdaptiveCardData = (data: Recognition) => {
    return {
      addCommentUrl: `${getDomain(business?.subdomain)}/recognition/polls/recognition/${data.uuid}?utm_source=microsoft_teams&utm_medium=adaptive_card_cta`,
      title: formData.message,
      companyValues: formData.companyValues?.map(({name}) => name),
      usersRecognised: formData.users?.map(({uuid, name}) => ({uuid, name})),
      recognisedBy: {
        uuid: currentUser?.uuid,
        name: (currentUser?.firstName || '') + ' ' + (currentUser?.lastName || '')
      }
    }
  }


  const onSubmit = async (e: BaseSyntheticEvent) => {
    e.preventDefault()

    try {
      if (!validateForm(formData)) {
        return
      }

      setSubmitted(true)

      const data = await recognitionService.createRecognition({
        companyValues: formData.companyValues?.map((value) => value.uuid)!,
        isPrivate: false,
        message: formData.message!,
        source: RecognitionSource.MicrosoftTeams,
        occasionId: formData.occasionId!,
        userIds: formData.users?.map((user) => user.uuid)!,
        teamIds: []
      })

      app.getContext().then((ctx) => {
        let type = "bot_scheduled_nudge"

        if ((ctx.channel && ctx.team) || ctx.chat) {
          type = "task_module"
        }

        trackingService.recogniseButtonClick({type: type})
      })

      const adaptiveCardData = getAdaptiveCardData(data)

      submitTask({
        action: "create",
        data: adaptiveCardData
      })
    } catch (error: any) {
      setSubmitted(false)

      throw error
    }
  }

  const onClose = async (e: BaseSyntheticEvent) => {
    e.preventDefault()

    try {
      setSubmitted(true)

      trackingService.closeButtonClick({type: "cancel"})
      submitTask({
        action: "close",
        data: {}
      })
    } finally {
      setSubmitted(false)
    }
  }

  const onCompanyValuesChange = (companyValues: Pair[]) => {
    setFormData({...formData, companyValues})
  }

  const onUsersChanged = (users: Pair[]) => {
    setValidationErrors({...validationErrors, isUserListEmpty: false})
    setFormData({...formData, users})
  }

  const onMessageChanged = (message: string) => {
    setValidationErrors({...validationErrors, isMessageEmpty: false})
    setFormData({...formData, message})
  }

  const onOccasionChanged = (occasionId: string) => {
    setFormData({...formData, occasionId})
  }

  const onSearchQueryChange = debounce((searchQuery: string) => {
    if (business?.uuid) {
      recognitionService.getUsers(business.uuid, searchQuery).then((result) => {
        const filteredUsers = result.filter((user) => user.uuid !== currentUser?.uuid)
        setUsers(filteredUsers)
      })
    }
  }, 500)

  return (
    <CustomForm onSubmit={onSubmit}>
      <FieldsContainer>
        <Users error={!!validationErrors.isUserListEmpty} disabled={submitted}
               users={users}
               onUserChange={onUsersChanged}
               onSearchQueryChange={onSearchQueryChange}
        />
        <RecognitionMessage error={!!validationErrors.isMessageEmpty} disabled={submitted}
                            onMessageChanged={onMessageChanged}/>
        <CompanyCelebration disabled={submitted} occasions={occasions} onOccasionChange={onOccasionChanged}/>
        <CompanyValues disabled={submitted} companyValues={companyValues} businessName={business?.businessName}
                       onCompanyValuesChange={onCompanyValuesChange}/>
        <FormLabel>
          Shared with everyone from your company on Perkbox
        </FormLabel>
      </FieldsContainer>
      <CtaContainer>
        <CancelButtonWrapper>
          <Button type="button" content="Close" onClick={onClose} disabled={submitted}/>
        </CancelButtonWrapper>
        <Button type="submit" content="Recognise" onClick={onSubmit} primary disabled={submitted}/>
      </CtaContainer>
    </CustomForm>
  )
}

export default RecognitionForm
